// A file for react-query mutations & queries related to auth pages
import type { ApiResponse, LoginFormData } from '@shared/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import type { MutableRefObject } from 'react'
import type ReCAPTCHA from 'react-google-recaptcha'
import { useAuth } from 'v2/shared/hooks/use-auth'
import { authPanelSignalHandlers, completeAfterBurst } from 'v2/shared/layouts/auth-panel-signal'
import { authService } from 'v2/shared/services/auth-service'
import { hodorService } from 'v2/shared/services/hodor-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { getSettingsQueryKeys } from 'v2/shared/utils/query-key-management'
import type {
  CompleteInviteSignupPayload,
  CompleteSignupPayload,
  CreateOrganizationPayload,
  InitiateSignupPayload,
  PreSignupCheckResponse,
  RCompleteSignupResponse,
  ResendActivationEmailPayload,
  RInviteInternalUserPayload,
  RInviteInternalUserResponse,
  RLoginResponse,
  RSsoAuthorizePayload,
  RSupportUserStatusPayload,
  RSupportUserStatusResponse,
  RUserChangePasswordPayload,
  RValidateInviteSignupTokenResponse,
  TwoFactorVerifyLoginPayload,
  TwoFactorVerifySignupPayload,
} from 'v2/types/api/auth'
import { AnalyticsEvents, track } from 'v2/utils/analytics'
import dayjs from 'v2/utils/dayjs'
import { identifySignupDevice, type SignupDevice } from 'v2/utils/fingerprint'
import { logger } from 'v2/utils/logger'
import { errorToast } from 'v2/utils/toasts'
import type { InviteUserForm } from '../settings/team-setup/users.config'
import { redirectToSso } from './utils/redirect-to-sso'
import { createUserSession, useSessionManager } from './utils/session-utils'

// Login Mutation - used in Login Page to authenticate user
export const useLoginMutation = (recaptchaRef: React.MutableRefObject<ReCAPTCHA>) => {
  const { completeLoginProcess } = useSessionManager()

  const mutation = useMutation({
    mutationFn: async (data: LoginFormData) => {
      const token = await recaptchaRef.current?.executeAsync()
      const { isSignup, ...restData } = data
      const payload = {
        ...restData,
        is_signup: isSignup ?? false,
        timezone: dayjs.tz.guess(),
        'g-recaptcha-response': token || '',
      }
      const response = await authService.login(payload)

      return response
    },
    onSuccess: (responseData, variables) => {
      // Same endpoint as V2 on hodor: Require SSO answers with the SSO URL instead of a session.
      if (responseData?.data?.sso_required) {
        redirectToSso(responseData.data.sso_login_url)
        return
      }
      // If 2FA is required, don't proceed with normal login flow
      if (!variables.isSignup && responseData?.data?.two_fa_enabled) {
        return
      }
      if (
        responseData?.data?.auth_token &&
        responseData?.data?.aom_uuid &&
        responseData?.data?.roles?.length
      ) {
        // Complete the login process using shared utility
        completeLoginProcess(responseData.data)

        // Handle navigation parameters (this remains specific to login flow)
        const nextParamsArr = window.location.search.split('next_url=')
        const nextParams = nextParamsArr.length > 1 ? nextParamsArr[1] : ''

        const returnToArr = window.location.search.split('return_to=')
        const returnTo = returnToArr.length > 1 ? returnToArr[1] : ''

        const searchParams = new URLSearchParams()
        searchParams.append('auth_token', responseData.data.auth_token)
        searchParams.append('aom_uuid', responseData.data.aom_uuid)
        searchParams.append('session_id', responseData.data.browser_session_id || '')
        searchParams.append('roles', responseData.data.roles.join(','))
        searchParams.append('next_url', nextParams)
        searchParams.append('return_to', returnTo)
        searchParams.append('region', responseData.data.region || '')
        searchParams.append('email', responseData.data.email || '')
        searchParams.append('org_name', responseData.data.organization_name || '')
        searchParams.append('name', responseData.data.name || '')
      }
    },
  })

  return mutation
}

// Login Mutation V2 - used in Login Page to authenticate user
export const useLoginMutationV2 = (recaptchaRef: React.MutableRefObject<ReCAPTCHA>) => {
  const { completeLoginProcess } = useSessionManager()

  const mutation = useMutation<ApiResponse<RLoginResponse>, AxiosError, LoginFormData>({
    mutationFn: async (data: LoginFormData) => {
      const token = await recaptchaRef.current?.executeAsync()
      const { isSignup, ...restData } = data
      const payload = {
        ...restData,
        is_signup: isSignup ?? false,
        timezone: dayjs.tz.guess(),
        'g-recaptcha-response': token || '',
      }
      return authService.loginv2(payload)
    },
    ...authPanelSignalHandlers,
    onSuccess: async (responseData, variables) => {
      // Require SSO: hodor returned the SSO URL instead of a session (admins are exempt server-side).
      if (responseData?.data?.sso_required) {
        redirectToSso(responseData.data.sso_login_url)
        return
      }
      // If 2FA is required, don't proceed with normal login flow
      if (!variables.isSignup && responseData?.data?.two_fa_enabled) {
        return
      }
      if (
        responseData?.data?.auth_token &&
        responseData?.data?.aom_uuid &&
        responseData?.data?.roles?.length
      ) {
        // Complete the login process using shared utility, after the auth panel's success burst
        await completeAfterBurst(() => completeLoginProcess(responseData.data))

        // Handle navigation parameters (this remains specific to login flow)
        const nextParamsArr = window.location.search.split('next_url=')
        const nextParams = nextParamsArr.length > 1 ? nextParamsArr[1] : ''

        const returnToArr = window.location.search.split('return_to=')
        const returnTo = returnToArr.length > 1 ? returnToArr[1] : ''

        const searchParams = new URLSearchParams()
        searchParams.append('auth_token', responseData.data.auth_token)
        searchParams.append('aom_uuid', responseData.data.aom_uuid)
        searchParams.append('session_id', responseData.data.browser_session_id || '')
        searchParams.append('roles', responseData.data.roles.join(','))
        searchParams.append('next_url', nextParams)
        searchParams.append('return_to', returnTo)
        searchParams.append('region', responseData.data.region || '')
        searchParams.append('email', responseData.data.email || '')
        searchParams.append('org_name', responseData.data.organization_name || '')
        searchParams.append('name', responseData.data.name || '')
      }
    },
  })

  return mutation
}

export const useSsoAuthorizeMutation = () => {
  const mutation = useMutation({
    mutationFn: async (data: RSsoAuthorizePayload) => {
      // A signup identifies the device first (bounded wait, never blocks); the ids ride
      // in hodor's OAuth state and come back on the SSO redirect.
      const device = data.flow === 'signup' ? await identifySignupDevice() : undefined
      const response = await hodorService.authorize({ ...data, ...device })
      return { response, device }
    },
    onSuccess: ({ response, device }, variables) => {
      const ssoUrl = response?.data?.url
      if (!ssoUrl) {
        // SSO not available for this account (e.g. sso_enabled: false with no url).
        errorToast({
          title: 'Single sign-on unavailable',
          description:
            'Single sign-on is not set up for this account. Please use email and password, or contact your admin.',
        })
        return
      }
      // Once hodor has accepted, like the email leg, so both funnel legs count the same thing.
      // sendBeacon: the redirect on the next line unloads the page.
      if (variables.flow === 'signup') {
        track(
          AnalyticsEvents.SIGNUP_INITIATED,
          { region: variables.region, sso: variables.sso_type, ...device },
          { transport: 'sendBeacon' },
        )
      }
      redirectToSso(ssoUrl)
    },
    onError: () => {
      errorToast({
        title: 'Could not start sign-in',
        description: 'Something went wrong starting single sign-on. Please try again.',
      })
    },
  })
  return mutation
}

export const useInviteUserV2Mutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: InviteUserForm) => authService.inviteUserV2(data),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().users })
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().profile })
    },
  })
}

// Refetch cadence is tied to the granularity of the displayed countdown so the
// query refetch itself keeps the visible "Xd Yh / Xh Ym / Xm left" text in step
// with reality, without a separate per-minute UI tick.
//   - Days visible (≥ 24h)   → every 1 hour
//   - Hours visible (1h–24h) → every 5 min
//   - Minutes only, ≥ 15m    → every 5 min
//   - Minutes only, < 15m    → every 1 min
const computeSupportStatusRefetchInterval = (
  expiresAtSec: number | null | undefined,
): number | false => {
  if (!expiresAtSec) return false // no active support user → don't poll
  const expiry = dayjs.unix(expiresAtSec)
  const now = dayjs()
  if (!expiry.isValid() || expiry.isBefore(now)) return false // already expired
  const minutesLeft = expiry.diff(now, 'minute')
  if (minutesLeft >= 24 * 60) return 60 * 60_000 // days visible: 1 hour
  if (minutesLeft >= 15) return 5 * 60_000 // hours or "≥15m" minutes-only: 5 min
  return 60_000 // < 15 min: 1 min
}

export const useSupportUserStatusQuery = (region: string, enabled = true) =>
  useQuery<ApiResponse<RSupportUserStatusResponse>, AxiosError>({
    queryKey: [...getSettingsQueryKeys().supportUserStatus, region],
    queryFn: () => authService.getSupportUserStatus({ region } as RSupportUserStatusPayload),
    enabled: enabled && !!region,
    refetchInterval: (query) =>
      computeSupportStatusRefetchInterval(query.state.data?.data?.expires_at),
    staleTime: 60_000, // 1 min — avoid back-to-back refetches on rapid navigation
    retry: false,
    refetchOnWindowFocus: false,
  })

export const useInviteInternalUserMutation = () => {
  const queryClient = useQueryClient()
  return useMutation<
    ApiResponse<RInviteInternalUserResponse>,
    AxiosError,
    RInviteInternalUserPayload
  >({
    mutationFn: (data) => authService.inviteInternalUser(data),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().supportUserStatus })
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().users })
    },
  })
}
export const useActivateInvitationV2Mutation = (
  recaptchaRef: React.MutableRefObject<ReCAPTCHA>,
) => {
  return useMutation<ApiResponse<RLoginResponse>, AxiosError, ActivateInvitationData>({
    mutationFn: async (data: ActivateInvitationData) => {
      const token = await recaptchaRef.current?.executeAsync()
      const payload = {
        ...data,
        timezone: dayjs.tz.guess(),
        'g-recaptcha-response': token || '',
      }
      return authService.activateV2(payload)
    },
  })
}
export const usePreSignupCheckQuery = (couponCode?: string) => {
  // Browser timezone is sent so the backend can fall back to it when IP
  // country lookup fails. Wrapped in try/catch because Intl can be stubbed
  // or missing in rare environments.
  let tz = ''
  try {
    tz = dayjs.tz.guess() || ''
  } catch {
    tz = ''
  }
  return useQuery<ApiResponse<PreSignupCheckResponse>>({
    queryKey: ['pre-signup-check', tz, couponCode],
    queryFn: () => authService.preSignupCheck(tz, couponCode),
    retry: 2,
    // Auto-recover when the user disconnects their VPN (SER-5734): React Query
    // re-runs the check when the tab regains focus (returning from the VPN
    // app/settings) or when the browser reconnects. staleTime 0 makes those
    // triggers always refetch instead of reusing the stale "blocked" result.
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  })
}

// Resolves to the Fingerprint ids sent with the request, so the page can put them on the analytics event.
export const useInitiateSignupMutation = (recaptchaRef: React.MutableRefObject<ReCAPTCHA>) =>
  useMutation<SignupDevice | undefined, AxiosError, InitiateSignupPayload>({
    mutationFn: async (data) => {
      const [token, device] = await Promise.all([
        recaptchaRef.current?.executeAsync(),
        identifySignupDevice(data.email),
      ])
      const payload = { ...data, ...device, 'g-recaptcha-response': token || '' }
      await authService.initiateSignup(payload)
      return device
    },
  })

// Re-sends the activation email for a pending-activation self-signup (SER-6348).
// The backend answers with the same generic 200 whether or not the account
// exists; only recaptcha (400) and rate-limit (429) rejections surface as errors.
export const useResendActivationEmailMutation = (recaptchaRef: React.MutableRefObject<ReCAPTCHA>) =>
  useMutation<ApiResponse<void>, AxiosError, { email: string }>({
    mutationFn: async (data) => {
      const token = await recaptchaRef.current?.executeAsync()
      const payload: ResendActivationEmailPayload = {
        ...data,
        'g-recaptcha-response': token || '',
      }
      return authService.resendActivationEmail(payload)
    },
  })

export const useCompleteSignupMutation = () => {
  return useMutation<ApiResponse<RCompleteSignupResponse>, AxiosError, CompleteSignupPayload>({
    mutationFn: async (data) => {
      return authService.completeSignup(data)
    },
  })
}

export const useValidateInviteSignupTokenQuery = (token: string | null) =>
  useQuery<ApiResponse<RValidateInviteSignupTokenResponse>, AxiosError>({
    queryKey: ['validate-invite-signup-token', token],
    queryFn: () => authService.validateInviteSignupToken(token ?? ''),
    enabled: !!token,
    retry: false,
    staleTime: 5 * 60 * 1000,
    gcTime: 0,
  })

export const useCompleteInviteSignupMutation = () =>
  useMutation<ApiResponse<RLoginResponse>, AxiosError, CompleteInviteSignupPayload>({
    mutationFn: (data) => authService.completeInviteSignup(data),
  })

export const useCreateOrganizationMutation = () => {
  const { logoutSuccess, loginSuccess } = useAuth()

  return useMutation({
    mutationFn: async (payload: CreateOrganizationPayload) => {
      const response = await authService.createOrganization(payload)
      return response.data
    },
    onSuccess: async (data) => {
      try {
        // Fire the capture FIRST, while posthog still has the current-session
        // identity. `transport: 'sendBeacon'` asks the browser to guarantee
        // delivery across the upcoming reload without racing a setTimeout.
        track(
          AnalyticsEvents.ORG_CREATED,
          {
            org_name: data.organization_name,
            region: data.region,
          },
          { transport: 'sendBeacon' },
        )
        await logoutSuccess()
        const userSession = createUserSession(data, data.email)
        loginSuccess(userSession)
        window.location.reload()
      } catch (error) {
        logger.error('Error creating organization', error)
        errorToast({
          title: 'Failed to set up organization',
          description: 'Please refresh the page or sign out and sign back in.',
        })
      }
    },
    onError: (error) => {
      errorToast({
        title: 'Failed to create organization',
        description: getErrorText(error as AxiosError<ApiResponse<unknown>>) || error.toString(),
      })
    },
  })
}

interface ActivateInvitationData {
  token: string
  region: string
  check_token: boolean
  first_name?: string
  last_name?: string
  password?: string
  timezone?: string
}

interface ForgotPasswordData {
  email: string
}

interface ResetPasswordData {
  password: string
  token: string
}

interface ValidateTokenData {
  token: string
}

export const useTwoFactorAuthMutation = (recaptchaRef: React.MutableRefObject<ReCAPTCHA>) => {
  const mutation = useMutation({
    mutationFn: async (data: {
      phone_number: string
      method: 'sms' | 'voice'
      aom_uuid: string
      org_id: number
      is_signup: boolean
      region: string
      email: string
    }) => {
      const token = await recaptchaRef.current?.executeAsync()
      const payload = {
        phone_number: data.phone_number,
        method: data.method,
        aom_uuid: data.aom_uuid,
        org_id: data.org_id,
        is_signup: data.is_signup,
        region: data.region,
        email: data.email,
        'g-recaptcha-response': token || '',
      }
      return authService.twoFactorAuth(payload)
    },
  })

  return mutation
}

/**
 * Two-factor verification mutation specifically for login flow
 * Handles session completion and cookie setting for authenticated users
 */
export const useTwoFactorVerifyLoginMutation = () => {
  const { completeLoginProcess } = useSessionManager()

  const mutation = useMutation({
    mutationFn: async (data: TwoFactorVerifyLoginPayload) => {
      const payload = {
        session_uuid: data.session_uuid,
        method: data.method,
        aom_uuid: data.aom_uuid,
        account_id: data.account_id,
        token: data.token,
        is_signup: false,
        is_authenticator: data.is_authenticator,
        actor_email: data.email,
        verification_pending: data.verification_pending,
        region: data.region,
        browser_session_id: data.originalLoginResponse.browser_session_id,
      }

      const response = (await authService.twoFactorVerify(payload)) as unknown as {
        data?: { auth_token?: string; browser_session_id?: string }
      }

      // SER-3764: auth_token is now returned from verify-otp (not from login response)
      const verifyData = response?.data
      const loginResponse = {
        ...data.originalLoginResponse,
        auth_token: verifyData?.auth_token || data.originalLoginResponse.auth_token,
        browser_session_id:
          verifyData?.browser_session_id || data.originalLoginResponse.browser_session_id,
      }
      await completeAfterBurst(() => completeLoginProcess(loginResponse, data.email))

      return response
    },
    ...authPanelSignalHandlers,
    onSuccess: async () => {},
  })

  return mutation
}
export const useTwoFactorVerifySignupMutation = () => {
  const { completeLoginProcess } = useSessionManager()

  const mutation = useMutation({
    mutationFn: async (data: TwoFactorVerifySignupPayload) => {
      const payload = {
        session_uuid: data.session_uuid,
        method: data.method,
        aom_uuid: data.aom_uuid,
        account_id: data.account_id,
        token: data.token,
        is_signup: true,
        is_authenticator: data.is_authenticator,
        actor_email: data.actor_email,
        verification_pending: data.verification_pending,
        region: data.region,
      }

      return authService.twoFactorVerify(payload) as unknown as Promise<ApiResponse<RLoginResponse>>
    },
    onSuccess: (response) => {
      completeLoginProcess(response.data)
    },
  })

  return mutation
}

export const useTwoFactorResendMutation = () => {
  const mutation = useMutation({
    mutationFn: async (data: {
      account_id: number
      method: 'sms' | 'voice'
      aom_uuid: string
      is_signup: boolean
      region: string
    }) => {
      const payload = {
        account_id: data.account_id,
        method: data.method,
        aom_uuid: data.aom_uuid,
        is_signup: data.is_signup,
        region: data.region,
      }
      return authService.twoFactorResend(payload)
    },
    onSuccess: () => {},
  })

  return mutation
}

// Password V2 Mutations
export const useChangePasswordMutationV2 = () =>
  useMutation({
    mutationFn: async (password: RUserChangePasswordPayload) => {
      const response = await authService.changePasswordV2(password)
      return response.data
    },
  })

export const useForgotPasswordMutationV2 = (recaptchaRef: MutableRefObject<ReCAPTCHA>) =>
  useMutation<ApiResponse<void>, AxiosError, ForgotPasswordData>({
    mutationFn: async (data) => {
      const token = await recaptchaRef.current?.executeAsync()
      const payload = {
        email: data.email,
        'g-recaptcha-response': token || '',
      }
      return authService.forgotPasswordV2(payload)
    },
  })

export const useResetPasswordMutationV2 = (recaptchaRef: MutableRefObject<ReCAPTCHA>) =>
  useMutation<ApiResponse<void>, AxiosError, ResetPasswordData>({
    mutationFn: async (data) => {
      const token = await recaptchaRef.current?.executeAsync()
      const payload = {
        ...data,
        'g-recaptcha-response': token || '',
      }
      return authService.resetPasswordV2(payload)
    },
  })

export const useValidateResetTokenMutationV2 = () =>
  useMutation<ApiResponse<void>, AxiosError, ValidateTokenData>({
    mutationFn: (data) => authService.validateResetTokenV2({ ...data, check_token: true }),
  })
